import { HttpHeaders } from "@angular/common/http";
import { Injectable, Signal, computed, inject, signal } from "@angular/core";
import { BitsApis } from "@app/core/APIs/bits-apis";
import { TerminologyService } from "@app/shared/services/terminology.service";
import { CrudApiOptions, CrudApiService, GenResp } from "@ez-library/services/crud-api.service";
import { InputSelectOption } from "@ez-library/types/Input-select-option";
import { Observable, catchError, forkJoin, map, of, shareReplay, tap } from "rxjs";
import { PHMS_INTAKE_FIELD_USAGES } from "./phms-intake.terminology";

export const PHMS_INTAKE_CONFIGURATION_CODE = "phms-runtime";
export const PHMS_INTAKE_PROGRAM_SCOPE_KEY_DOMAIN = "program-scope-key";
export const PHMS_INTAKE_SCOPE_TYPE_CODE = "jurisdiction-program";
export const PHMS_INTAKE_SCOPE_KEY = "ca/phms";
export const PHMS_INTAKE_FIELD_TOKENS = {
    incarceratedPersonDisplayName: PHMS_INTAKE_FIELD_USAGES.incarceratedPersonDisplayName.token.code,
    incarceratedPersonIdentifier: PHMS_INTAKE_FIELD_USAGES.incarceratedPersonIdentifier.token.code,
    programScopeKey: PHMS_INTAKE_FIELD_USAGES.programScopeKey.token.code,
} as const;

export const PHMS_INTAKE_FIELD_DEFAULTS: Record<keyof typeof PHMS_INTAKE_FIELD_TOKENS, string> = {
    incarceratedPersonDisplayName: PHMS_INTAKE_FIELD_USAGES.incarceratedPersonDisplayName.fallbackText,
    incarceratedPersonIdentifier: PHMS_INTAKE_FIELD_USAGES.incarceratedPersonIdentifier.fallbackText,
    programScopeKey: PHMS_INTAKE_FIELD_USAGES.programScopeKey.fallbackText,
};

interface PhmsLookupCatalogValue {
    valueCode: string;
    displayText: string;
    sortOrder: number;
    isEnabled: boolean;
}

interface PhmsLookupCatalogResponse {
    values: PhmsLookupCatalogValue[];
}

const requestHeaders = new HttpHeaders({
    Accept: "application/json, text/plain, */*",
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
});

const apiOptions: Partial<CrudApiOptions<"body", "json">> = {
    retries: 0,
    feedbackOptions: { successMode: "none", errorMode: "none" },
    getOptions: {
        responseType: "json",
        headers: requestHeaders,
        withCredentials: true,
    },
};

@Injectable({ providedIn: "root" })
export class PhmsIntakeRuntimeService {
    private readonly bitsApis = inject(BitsApis);
    private readonly crudService = inject(CrudApiService);
    private readonly terminology = inject(TerminologyService);

    private readonly programScopeKeyOptionsState = signal<InputSelectOption[]>([]);
    private programScopeKeyRequest$?: Observable<InputSelectOption[]>;

    readonly programScopeKeyOptions: Signal<InputSelectOption[]> = computed(() => this.programScopeKeyOptionsState());
    readonly incarceratedPersonDisplayNameLabel: Signal<string> = computed(() =>
        this.resolveFieldLabel("incarceratedPersonDisplayName"),
    );
    readonly incarceratedPersonIdentifierLabel: Signal<string> = computed(() =>
        this.resolveFieldLabel("incarceratedPersonIdentifier"),
    );
    readonly programScopeKeyLabel: Signal<string> = computed(() => this.resolveFieldLabel("programScopeKey"));

    loadProgramScopeKeyOptions(): Observable<InputSelectOption[]> {
        if (!this.programScopeKeyRequest$) {
            const url = this.bitsApis.core.manual.getPlatformConfigurationLookupCatalog(
                PHMS_INTAKE_CONFIGURATION_CODE,
                PHMS_INTAKE_PROGRAM_SCOPE_KEY_DOMAIN,
                {
                    scopeTypeCode: PHMS_INTAKE_SCOPE_TYPE_CODE,
                    scopeKey: PHMS_INTAKE_SCOPE_KEY,
                },
            );

            this.programScopeKeyRequest$ = this.crudService.getbyUrl<PhmsLookupCatalogResponse>(url, apiOptions).pipe(
                map((response) => unwrapResponse(response)),
                map((catalog) => toProgramScopeKeyOptions(catalog?.values ?? [])),
                tap((options) => this.programScopeKeyOptionsState.set(options)),
                catchError(() => {
                    this.programScopeKeyOptionsState.set([]);
                    return of<InputSelectOption[]>([]);
                }),
                shareReplay(1),
            );
        }

        return this.programScopeKeyRequest$;
    }

    loadFieldLabels(): Observable<Record<keyof typeof PHMS_INTAKE_FIELD_TOKENS, string>> {
        return forkJoin([
            this.terminology.loadUsage(PHMS_INTAKE_FIELD_USAGES.incarceratedPersonDisplayName),
            this.terminology.loadUsage(PHMS_INTAKE_FIELD_USAGES.programScopeKey),
        ]).pipe(
            map(() => ({
                incarceratedPersonDisplayName: this.resolveFieldLabel("incarceratedPersonDisplayName"),
                incarceratedPersonIdentifier: this.resolveFieldLabel("incarceratedPersonIdentifier"),
                programScopeKey: this.resolveFieldLabel("programScopeKey"),
            })),
        );
    }

    private resolveFieldLabel(fieldKey: keyof typeof PHMS_INTAKE_FIELD_TOKENS): string {
        return this.terminology.resolveUsage(PHMS_INTAKE_FIELD_USAGES[fieldKey]);
    }
}

function toProgramScopeKeyOptions(values: PhmsLookupCatalogValue[]): InputSelectOption[] {
    return values
        .filter((value) => value.isEnabled !== false)
        .slice()
        .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
        .map((value) => ({ value: value.valueCode, viewValue: value.displayText }));
}

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T | null {
    if (!response) {
        return null;
    }

    if (typeof response === "object" && response !== null && "data" in response) {
        return (response as GenResp<T>).data;
    }

    return response as T;
}
